/* ─────────────────────────────────────────────
   BKlay Studio — drawn vessels and the wheelhead.

   Every pot on the site is generated from a profile
   curve plus its real finished dimensions, which come
   off the product's metafields as data attributes.
   All the canvases on a page share one scale, so a
   22cm vase really does tower over a 9cm cup.

   A product with a photograph renders an <img> instead
   and never reaches this file — so the drawings quietly
   retire one at a time as the photography arrives.
   ───────────────────────────────────────────── */
(function () {
  "use strict";

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

  /* Profiles are [t, halfWidth] from rim (t=0) to foot (t=1),
     halfWidth as a fraction of that piece's own width, topping
     out at .500. Real centimetres arrive per-element. */
  var FORMS = {
    yunomi: { dip: .74,
              p: [[0,.500],[.20,.487],[.50,.468],[.78,.428],[1,.355]] },
    bowl:   { dip: .80,
              p: [[0,.500],[.14,.492],[.30,.470],[.48,.430],[.66,.372],[.84,.288],[1,.150]] },
    bottle: { dip: .55,
              p: [[0,.115],[.06,.110],[.14,.126],[.28,.276],[.44,.430],[.56,.500],[.72,.468],[.88,.338],[1,.245]] },
    mug:    { dip: .70, handle: true,
              p: [[0,.500],[.28,.495],[.62,.482],[1,.425]] },
    dish:   { dip: .85,
              p: [[0,.500],[.28,.480],[.62,.400],[1,.250]] },
    jar:    { dip: .78, lid: true,
              p: [[0,.285],[.10,.332],[.30,.462],[.52,.500],[.74,.418],[.90,.330],[1,.275]] },
    plate:  { dip: .90,
              p: [[0,.500],[.35,.470],[.7,.390],[1,.230]] },
    tumbler:{ dip: .72,
              p: [[0,.480],[.25,.490],[.6,.485],[1,.410]] }
  };

  var DEFAULT_FORM = "yunomi";

  /* Reading these off the document is the most expensive thing on the wheel's
     frame, and they only change when the theme does — so cache them, and let
     the two theme hooks at the bottom of this file clear the cache. */
  var tokenCache = null;

  function tokens() {
    if (tokenCache) return tokenCache;
    var cs = getComputedStyle(document.documentElement);
    tokenCache = {
      g1:   cs.getPropertyValue("--clay-glaze-1").trim(),
      g2:   cs.getPropertyValue("--clay-glaze-2").trim(),
      raw:  cs.getPropertyValue("--clay-raw").trim(),
      edge: cs.getPropertyValue("--clay-edge").trim(),
      shd:  cs.getPropertyValue("--clay-shadow").trim(),
      line: cs.getPropertyValue("--line").trim(),
      acc:  cs.getPropertyValue("--accent").trim()
    };
    return tokenCache;
  }

  function fit(canvas) {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    var bw = Math.round(r.width * dpr), bh = Math.round(r.height * dpr);
    if (canvas.width !== bw || canvas.height !== bh) {
      canvas.width = bw;
      canvas.height = bh;
    }
    var ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, r.width, r.height);
    return { ctx: ctx, w: r.width, h: r.height };
  }

  function num(el, attr, fallback) {
    var v = parseFloat(el.getAttribute(attr));
    return (isFinite(v) && v > 0) ? v : fallback;
  }

  function formOf(el) {
    return FORMS[(el.getAttribute("data-form") || "").toLowerCase()] || FORMS[DEFAULT_FORM];
  }

  /* ── the shared scale ───────────────────────────
     Expressed as a reference width in centimetres:
     each canvas draws at (its own width / reference)
     pixels per centimetre. Because every vessel canvas
     has the same aspect ratio, one reference keeps a
     card and a product page consistent with each other. */
  var REFERENCE_CM = 30;

  function measure(pieces) {
    var maxW = 1, maxH = 1;
    pieces.forEach(function (el) {
      maxW = Math.max(maxW, num(el, "data-w", 10));
      maxH = Math.max(maxH, num(el, "data-h", 10));
    });
    /* 0.88 leaves a margin either side; 0.861 is the vertical
       equivalent once the 4:4.1 canvas ratio is accounted for.
       The floor stops a lone small cup on its own product page
       from being blown up to the size of a dinner plate. */
    REFERENCE_CM = Math.max(maxW / 0.88, maxH / 0.861, 12);
  }

  /* ── the studio spiral ────────────────────────────
     The mark is a plain Archimedean spiral — measured off
     bklay-spiral-stone.svg as r = 7.104 + 2.303·theta over
     5.40 turns, tapering 4.60 to 1.73. Drawn here rather
     than loaded as an image so it can turn with the wheel
     and take the clay tokens.

     Canvas carries one lineWidth per stroke, so a tapering
     line cannot be stroked in one pass. This walks both
     edges of the ribbon instead and fills the outline once
     — a single fill per frame, where stroking each segment
     separately would be a few hundred. */
  var SP_A = 7.104, SP_B = 2.303, SP_TH0 = -Math.PI / 2;

  function fillSpiral(ctx, cx, cy, R, turns, rot, w0, w1) {
    var th1 = SP_TH0 + turns * Math.PI * 2;
    var k = R / (SP_A + SP_B * th1);          /* fit the mark to the disc */
    var steps = Math.max(64, Math.round(turns * 44));
    var fwd = [], bwd = [], i;

    for (i = 0; i <= steps; i++) {
      var u = i / steps;
      var th = SP_TH0 + u * (th1 - SP_TH0);
      var r = (SP_A + SP_B * th) * k;
      var a = th + rot;
      var x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
      /* analytic tangent, so the ribbon keeps an even width
         through the tight turns at the eye */
      var tx = SP_B * k * Math.cos(a) - r * Math.sin(a);
      var ty = SP_B * k * Math.sin(a) + r * Math.cos(a);
      var m = Math.sqrt(tx * tx + ty * ty) || 1;
      var h = ((w0 + (w1 - w0) * u) * k) / 2;
      fwd.push([x - ty / m * h, y + tx / m * h]);
      bwd.push([x + ty / m * h, y - tx / m * h]);
    }

    ctx.beginPath();
    ctx.moveTo(fwd[0][0], fwd[0][1]);
    for (i = 1; i < fwd.length; i++) ctx.lineTo(fwd[i][0], fwd[i][1]);
    for (i = bwd.length - 1; i >= 0; i--) ctx.lineTo(bwd[i][0], bwd[i][1]);
    ctx.closePath();
    ctx.fill();
  }

  /* ── the wheelhead ────────────────────────────── */
  var spin = 0;

  function paintWheel(canvas) {
    var s = fit(canvas);
    if (!s) return;
    var t = tokens();
    var ctx = s.ctx, cx = s.w / 2, cy = s.h / 2;
    var R = Math.min(s.w, s.h) * 0.46;

    /* "rings" is the thrown wheelhead this started as; "spiral"
       swaps its throwing rings for the studio mark; "mark" drops
       the clay altogether and turns the mark alone. Anything
       unrecognised falls back to rings. */
    var mode = canvas.getAttribute("data-wheel") || "rings";

    if (mode === "mark") {
      ctx.fillStyle = t.acc;
      fillSpiral(ctx, cx, cy, R * 0.97, 5.4, spin, 4.6, 1.73);
      return;
    }

    var disc = ctx.createRadialGradient(cx - R * .25, cy - R * .3, R * .05, cx, cy, R);
    disc.addColorStop(0, t.g1);
    disc.addColorStop(1, t.g2);
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.fillStyle = disc;
    ctx.fill();

    /* throwing rings — closer together toward the rim,
       and each one a touch off centre, the way they are
       when the clay was never perfectly centred */
    var rings = mode === "spiral" ? 0 : 46;
    for (var i = 1; i <= rings; i++) {
      var f = Math.pow(i / rings, 0.82);
      var rr = R * f;
      var wob = (1 - f) * R * 0.02 + R * 0.004;
      var ox = Math.cos(spin + i * 0.38) * wob;
      var oy = Math.sin(spin + i * 0.38) * wob;
      ctx.beginPath();
      ctx.arc(cx + ox, cy + oy, rr, 0, Math.PI * 2);
      ctx.strokeStyle = t.edge;
      ctx.globalAlpha = 0.12 + 0.30 * Math.abs(Math.sin(i * 1.31));
      ctx.lineWidth = i % 5 === 0 ? 1.6 : 0.8;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    /* the mark, pressed into the clay in place of the rings */
    if (mode === "spiral") {
      ctx.fillStyle = t.edge;
      ctx.globalAlpha = .85;
      fillSpiral(ctx, cx, cy, R * 0.9, 5.4, spin, 4.6, 1.73);
      ctx.globalAlpha = 1;
    }

    /* the spindle the wheelhead turns on */
    ctx.beginPath();
    ctx.arc(cx, cy, R * 0.045, 0, Math.PI * 2);
    ctx.fillStyle = t.shd;
    ctx.fill();
    ctx.strokeStyle = t.edge;
    ctx.lineWidth = 1;
    ctx.stroke();

    /* wet clay catches the light on one side */
    var sheen = ctx.createRadialGradient(cx - R * .38, cy - R * .42, R * .02, cx - R * .1, cy - R * .1, R * 1.15);
    sheen.addColorStop(0, "rgba(255,255,255,.34)");
    sheen.addColorStop(.55, "rgba(255,255,255,.04)");
    sheen.addColorStop(1, "rgba(0,0,0,.10)");
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = sheen;
    ctx.fillRect(cx - R, cy - R, R * 2, R * 2);
    ctx.restore();

    /* the mark a rib leaves coming off the rim */
    ctx.beginPath();
    ctx.arc(cx, cy, R * 0.93, spin * .55, spin * .55 + 0.9);
    ctx.strokeStyle = t.acc;
    ctx.globalAlpha = .75;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.stroke();
    ctx.globalAlpha = 1;

    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.strokeStyle = t.edge;
    ctx.lineWidth = 1.25;
    ctx.stroke();
  }

  /* ── vessels ──────────────────────────────────── */
  function radiusAt(p, t) {
    for (var i = 0; i < p.length - 1; i++) {
      if (t <= p[i + 1][0]) {
        var a = p[i], b = p[i + 1];
        var u = (t - a[0]) / (b[0] - a[0] || 1);
        u = u * u * (3 - 2 * u);               /* smoothstep, so shoulders curve */
        return a[1] + (b[1] - a[1]) * u;
      }
    }
    return p[p.length - 1][1];
  }

  function vesselPath(ctx, form, cx, topY, height, width) {
    var N = 60, i, t, r;
    ctx.beginPath();
    for (i = 0; i <= N; i++) {
      t = i / N;
      r = radiusAt(form.p, t) * width;
      ctx[i === 0 ? "moveTo" : "lineTo"](cx + r, topY + t * height);
    }
    for (i = N; i >= 0; i--) {
      t = i / N;
      r = radiusAt(form.p, t) * width;
      ctx.lineTo(cx - r, topY + t * height);
    }
    ctx.closePath();
  }

  function paintPiece(canvas) {
    var s = fit(canvas);
    if (!s) return;

    var form = formOf(canvas);
    var t = tokens(), ctx = s.ctx;
    var dip = num(canvas, "data-dip", form.dip);

    var scale = s.w / REFERENCE_CM;            /* pixels per centimetre */
    var cx = s.w / 2;
    var baseY = s.h * 0.90;                    /* the shelf line */
    var height = num(canvas, "data-h", 10) * scale;
    var width = num(canvas, "data-w", 10) * scale;
    var topY = baseY - height;

    /* the shelf every piece is standing on */
    ctx.beginPath();
    ctx.moveTo(0, baseY + .5);
    ctx.lineTo(s.w, baseY + .5);
    ctx.strokeStyle = t.line;
    ctx.lineWidth = 1;
    ctx.globalAlpha = .8;
    ctx.stroke();
    ctx.globalAlpha = 1;

    var footR = radiusAt(form.p, 1) * width;
    var shadow = ctx.createRadialGradient(cx, baseY, 1, cx, baseY, footR * 3);
    shadow.addColorStop(0, t.shd);
    shadow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.save();
    ctx.scale(1, 0.16);
    ctx.beginPath();
    ctx.arc(cx, baseY / 0.16, footR * 2.6, 0, Math.PI * 2);
    ctx.fillStyle = shadow;
    ctx.fill();
    ctx.restore();

    /* handle first, so the body overlaps its join */
    if (form.handle) {
      var hTop = topY + height * 0.22, hBot = topY + height * 0.70;
      var hx = cx + radiusAt(form.p, 0.3) * width;
      ctx.beginPath();
      ctx.moveTo(hx - 1, hTop);
      ctx.bezierCurveTo(hx + width * .44, hTop + height * .02, hx + width * .44, hBot - height * .04, hx - 1, hBot);
      ctx.strokeStyle = t.g2;
      ctx.lineWidth = Math.max(5, width * .095);
      ctx.lineCap = "round";
      ctx.stroke();
    }

    /* raw clay body, then glaze dipped to the line */
    ctx.save();
    vesselPath(ctx, form, cx, topY, height, width);
    ctx.clip();

    ctx.fillStyle = t.raw;
    ctx.fillRect(0, 0, s.w, s.h);

    var glaze = ctx.createLinearGradient(cx - width * .3, topY, cx + width * .3, baseY);
    glaze.addColorStop(0, t.g1);
    glaze.addColorStop(1, t.g2);
    ctx.fillStyle = glaze;
    ctx.fillRect(0, topY - 4, s.w, height * dip + 4);

    /* where the dip stopped, glaze always pools a little thicker */
    ctx.globalAlpha = .35;
    ctx.fillStyle = t.edge;
    ctx.fillRect(0, topY + height * dip - 3, s.w, 3);
    ctx.globalAlpha = 1;

    /* light down the left flank */
    var flank = ctx.createLinearGradient(cx - width * .28, 0, cx + width * .1, 0);
    flank.addColorStop(0, "rgba(255,255,255,.20)");
    flank.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = flank;
    ctx.fillRect(0, 0, s.w, s.h);
    ctx.restore();

    ctx.lineWidth = 1;
    ctx.strokeStyle = t.edge;
    vesselPath(ctx, form, cx, topY, height, width);
    ctx.stroke();

    /* the opening */
    var rimR = radiusAt(form.p, 0) * width;
    ctx.beginPath();
    ctx.ellipse(cx, topY, rimR, Math.max(3, rimR * 0.26), 0, 0, Math.PI * 2);
    ctx.fillStyle = t.g2;
    ctx.fill();
    ctx.strokeStyle = t.edge;
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(cx, topY + Math.max(1.5, rimR * .07), rimR * .82, Math.max(2, rimR * 0.2), 0, 0, Math.PI * 2);
    ctx.fillStyle = t.shd;
    ctx.fill();

    if (form.lid) {
      ctx.beginPath();
      ctx.ellipse(cx, topY - height * .04, rimR * 1.18, rimR * .34, 0, Math.PI, 0);
      ctx.fillStyle = t.g1;
      ctx.fill();
      ctx.strokeStyle = t.edge;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, topY - height * .075, Math.max(3, rimR * .16), 0, Math.PI * 2);
      ctx.fillStyle = t.g2;
      ctx.fill();
      ctx.stroke();
    }
  }

  /* ── wiring ───────────────────────────────────── */
  var wheels = [], pieces = [], spinning = false;

  function collect() {
    wheels = [].slice.call(document.querySelectorAll("[data-wheel]"));
    pieces = [].slice.call(document.querySelectorAll("canvas.piece-fig, canvas.product-fig"));
    measure(pieces);
    watchSizes();
  }

  /* A canvas has to be redrawn whenever its box changes, and there are more
     ways for that to happen than are worth enumerating — switching between
     the card and list views, the window resizing, a late font landing, a
     container reflowing. Watching the elements themselves catches all of
     them. The earlier approach deferred a repaint by two animation frames
     after a view switch, which failed whenever the tab was not compositing:
     the frames simply never arrived and the pots stayed at the old size.
     Setting the backing store does not change layout, so this cannot loop. */
  var ro = null;

  function watchSizes() {
    if (typeof ResizeObserver === "undefined") return;
    if (!ro) {
      ro = new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var el = entries[i].target;
          if (el.hasAttribute("data-wheel")) paintWheel(el);
          else paintPiece(el);
        }
      });
    } else {
      ro.disconnect();
    }
    wheels.forEach(function (el) { ro.observe(el); });
    pieces.forEach(function (el) { ro.observe(el); });
  }

  function paintAll() {
    wheels.forEach(paintWheel);
    pieces.forEach(paintPiece);
  }

  function loop() {
    spin += 0.0022;
    wheels.forEach(paintWheel);
    requestAnimationFrame(loop);
  }

  function start() {
    collect();
    paintAll();
    if (!spinning && wheels.length && !reduced.matches) {
      spinning = true;
      requestAnimationFrame(loop);
    }
    reveal();
    themeSwitch.init();
    viewSwitch.init();
    filterSwitch.init();
    initNav();
  }

  /* repaint when the theme changes underneath us */
  function repaintTheme() { tokenCache = null; paintAll(); }

  var scheme = window.matchMedia("(prefers-color-scheme: dark)");
  if (scheme.addEventListener) { scheme.addEventListener("change", repaintTheme); }
  else if (scheme.addListener) { scheme.addListener(repaintTheme); }

  new MutationObserver(repaintTheme).observe(document.documentElement, {
    attributes: true, attributeFilter: ["data-theme"]
  });

  var resizeTimer;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(paintAll, 140);
  });


  /* ── scroll reveal ────────────────────────────── */
  var io = null;
  function reveal() {
    var targets = document.querySelectorAll(".rise:not(.in)");
    if (reduced.matches || !("IntersectionObserver" in window)) {
      [].forEach.call(targets, function (el) { el.classList.add("in"); });
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); }
        });
      }, { rootMargin: "0px 0px -8% 0px", threshold: .05 });
    }
    [].forEach.call(targets, function (el) { io.observe(el); });
  }

  /* ── one switch, three times over ─────────────
     The colour theme, the cards/list view and the archive's available-only
     filter are the same control wearing three hats: a button names a value,
     the value lands on <html> as an attribute, and the stylesheet does the
     rest. Nothing is re-rendered, so scroll position and the order of the
     pieces both survive being switched.

     Only three things actually differ — which attribute, whether the choice
     is worth remembering, and what counts as "off" — so those are the
     arguments and the mechanism is written once. It had been transcribed
     three times, and the copies had already drifted.

     The click listener is delegated so a switch inside a section Shopify has
     just re-rendered keeps working without being rebound. */
  function rootSwitch(opts) {
    var root = document.documentElement;
    var selector = "[" + opts.set + "]";

    function read() {
      var v = root.getAttribute(opts.attr);
      return opts.values.indexOf(v) > -1 ? v : opts.off;
    }

    function remember(v) {
      if (!opts.key) return;
      try {
        if (v === opts.off) localStorage.removeItem(opts.key);
        else localStorage.setItem(opts.key, v);
      } catch (e) {}
    }

    function sync() {
      var now = read();
      var buttons = document.querySelectorAll(selector);
      for (var i = 0; i < buttons.length; i++) {
        /* A toggle has one button standing for on-or-off; a chooser has one
           button per value, and only the current one is pressed. */
        var on = opts.toggle ? now !== opts.off : buttons[i].getAttribute(opts.set) === now;
        buttons[i].setAttribute("aria-pressed", on ? "true" : "false");
      }
    }

    function apply(v) {
      if (v === opts.off) root.removeAttribute(opts.attr);
      else root.setAttribute(opts.attr, v);
      remember(v);
      sync();
    }

    document.addEventListener("click", function (ev) {
      var btn = ev.target.closest ? ev.target.closest(selector) : null;
      if (!btn) return;
      ev.preventDefault();
      var wants = btn.getAttribute(opts.set);
      apply(opts.toggle && read() !== opts.off ? opts.off : wants);
    });

    return {
      sync: sync,
      /* The markup ships hidden, so a control never appears without the
         script that drives it. */
      init: function () {
        var groups = document.querySelectorAll(opts.reveal + "[hidden]");
        for (var i = 0; i < groups.length; i++) groups[i].removeAttribute("hidden");
        sync();
      }
    };
  }

  /* The stylesheet already handles all three theme states: an explicit choice
     stamps data-theme on <html>, and no attribute at all means follow the
     system. So "system" is simply the absence of a choice — which is exactly
     what `off` means here. The MutationObserver above catches the change and
     repaints every canvas in the new palette. */
  var themeSwitch = rootSwitch({
    attr: "data-theme", set: "data-theme-set", reveal: ".theme-switch",
    values: ["light", "dark"], off: "system", key: "bklay-theme"
  });

  /* No repaint is scheduled when the view changes, on purpose. Changing the
     attribute reflows the canvases and the ResizeObserver redraws each one at
     its new size as that happens — reliable in a way that waiting a couple of
     frames was not. Browsers without ResizeObserver fall back to the debounced
     resize handler and the next navigation. */
  var viewSwitch = rootSwitch({
    attr: "data-shelf-view", set: "data-view-set", reveal: ".view-switch",
    values: ["list"], off: "cards", key: "bklay-shelf-view"
  });

  /* Deliberately not remembered between visits, which is why it passes no key.
     The view switch is a standing preference about how someone likes to read a
     shelf; this is a question they asked once, and coming back to a silently
     shortened archive would be the wrong answer to it.

     The pieces it can hide are only the ones already on the page — which is why
     the section refuses to render the button once the archive is paginated. */
  var filterSwitch = rootSwitch({
    attr: "data-shelf-filter", set: "data-filter-set", reveal: ".archive-filter",
    values: ["open"], off: "", toggle: true
  });

  /* A stored theme was already applied by the inline script in the head; this
     only reconciles the buttons with it. */
  themeSwitch.sync();

  /* ── the masthead disclosure ────────────────────
     The markup ships with every link present and the toggle hidden, so a
     visitor without JS gets a nav that wraps rather than one that is empty.
     Collapsing is opt-in from here, once we know we can open it again. */
  function initNav() {
    var navs = document.querySelectorAll("[data-nav]");
    for (var i = 0; i < navs.length; i++) {
      navs[i].setAttribute("data-collapsible", "");
      var toggle = navs[i].querySelector("[data-nav-toggle]");
      if (toggle) toggle.removeAttribute("hidden");
    }
  }

  function setNav(nav, open) {
    if (!nav) return;
    var toggle = nav.querySelector("[data-nav-toggle]");
    if (open) nav.setAttribute("data-open", "");
    else nav.removeAttribute("data-open");
    if (toggle) toggle.setAttribute("aria-expanded", open ? "true" : "false");
  }

  function closeNavs(returnFocus) {
    var navs = document.querySelectorAll("[data-nav][data-open]");
    for (var i = 0; i < navs.length; i++) {
      setNav(navs[i], false);
      if (returnFocus) {
        var toggle = navs[i].querySelector("[data-nav-toggle]");
        if (toggle) toggle.focus();
      }
    }
  }

  document.addEventListener("click", function (ev) {
    if (!ev.target.closest) return;

    var toggle = ev.target.closest("[data-nav-toggle]");
    if (toggle) {
      var nav = toggle.closest("[data-nav]");
      setNav(nav, !nav.hasAttribute("data-open"));
      return;
    }

    /* A link inside the panel is a same-page anchor most of the time, and
       leaving the panel covering what it just scrolled to is the classic
       version of this bug. */
    if (ev.target.closest("[data-nav][data-open] .nav-links a")) {
      closeNavs(false);
      return;
    }

    if (!ev.target.closest("[data-nav][data-open]")) closeNavs(false);
  });

  document.addEventListener("keydown", function (ev) {
    if (ev.key === "Escape" || ev.key === "Esc") closeNavs(true);
  });

  /* Widening past the breakpoint puts the links back in the masthead; an
     open panel left behind would be a second copy of them. */
  var navWide = window.matchMedia("(min-width: 46.01rem)");
  if (navWide.addEventListener) navWide.addEventListener("change", function () { closeNavs(false); });
  else if (navWide.addListener) navWide.addListener(function () { closeNavs(false); });

  /* Shopify swaps section markup in and out while she edits,
     so anything drawn has to be drawn again afterwards. */
  document.addEventListener("shopify:section:load", start);
  document.addEventListener("shopify:section:reorder", start);
  document.addEventListener("shopify:section:unload", collect);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
